import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

// Components
import Heading from '../../../Heading';
import ReplacementSlider from '../../../ReplacementSlider';
import Button from '../../../Button';
import Gift from '../Gift';

// State
import { removeItem } from '../../../../state/modules/cart/actions';
import { addToCart as addToCartCatalog } from '../../../../state/modules/catalog/actions';
import { addToCart as addToCartSailPlay } from '../../../../state/modules/sailplay/actions';
import { showModalConfirm } from '../../../../state/modules/modalConfirm/actions';
import { clearStock, reapplyStock } from '../../../../state/modules/stock/actions';

// Icons
import Cheese from '../../../../icons/cheese.svg';
import RemoveIcon from '../../../../icons/close.svg';

// Router
import { getRoute } from '../../../../entry/routes';

// Utils
import { getStockConditionGoods } from '../../../../state/modules/stock/selectors';
import { filteredGiftsByStopListSelector } from '../../../../state/modules/sailplay/selectors';

// Styles
import styles from './ViewInStopList.styl';

const cx = classNames.bind(styles);

const isSockCondition = (goods, id) =>
  goods.some(good => (good?.variations || []).find(variation => variation.id === id));

export default function ViewInStopList(props) {
  const { className, good } = props;
  const { count, hash, item, type, updating, removing, replacements } = good;
  const { id, image, name } = item;

  const dispatch = useDispatch();
  const stockConditionGoods = useSelector(getStockConditionGoods);
  const stock_code = useSelector(state => state.stock.data?.code);
  const { isAllConditionGoodsInStopList, isAllRewardsInStoplist } = useSelector(
    state => state.stock,
  );
  const gifts = useSelector(filteredGiftsByStopListSelector);
  const isPhone = useSelector(state => state.responsive.isPhone);
  const [isSliderOpen, setIsSliderOpen] = useState(false);

  // Types
  const isGift = type === 'gift';
  const isBonus = type === 'bonus';
  const isStockCondition = isSockCondition(stockConditionGoods, id);

  const addToCart = isStockCondition ? addToCartSailPlay : addToCartCatalog;

  const to = isGift
    ? `${getRoute('stockGifts', 0, stock_code)}?replace`
    : `${getRoute('stockConditions', 1, stock_code)}?replace=true&item=${hash}`;

  const replacementsVariation = isBonus ? gifts : replacements;

  const onRemove = () => {
    dispatch(removeItem(hash));
  };

  const sliderIsOpenToggle = () => {
    setIsSliderOpen(prev => !prev);
  };

  const showModalHandler = messageId => {
    dispatch(
      showModalConfirm({
        btnConfirmText: <FormattedMessage id="button.delete" />,
        btnCancelText: <FormattedMessage id="button.cancel" />,
        message: <FormattedMessage id={messageId} />,
        onConfirm: () => dispatch(clearStock()),
      }),
    );
  };

  const removeItemHandler = () => {
    sliderIsOpenToggle();
    dispatch(removeItem(hash));
  };

  const onCLickHandlet = () => {
    if (isGift && isAllRewardsInStoplist) {
      return showModalHandler('modal.stockNoRewards.text');
    }

    if (isStockCondition && isAllConditionGoodsInStopList) {
      return showModalHandler('modal.stockNoConditionGoods.text');
    }

    return sliderIsOpenToggle();
  };

  const currentClassType = isPhone ? 'Mobile' : 'Desktop';

  useEffect(() => {
    if ((isGift || isStockCondition) && stock_code) {
      dispatch(reapplyStock({ stock_code }));
    }
  }, []);

  const ReplacementView =
    replacementsVariation.length > 0 ? (
      <ReplacementSlider
        replacements={replacementsVariation}
        addToCart={addToCart}
        removeItem={removeItemHandler}
        onCloseBtnClick={sliderIsOpenToggle}
        type={type}
        count={count}
      />
    ) : (
      <div className={cx(`CartItem__inStopList_${currentClassType}-noReplacements`)}>
        <Cheese width={54} height={54} />
        <span>
          <FormattedMessage id="order.replacements404" />
        </span>
        <Button secondary small to={getRoute('home')} onClick={removeItemHandler}>
          <FormattedMessage id="button.goToMenu" />
        </Button>
      </div>
    );

  return isSliderOpen ? (
    ReplacementView
  ) : (
    <div className={cx(`CartItem__inStopList_${currentClassType}`)}>
      {(updating || removing) && (
        <div className={cx('CartItem__loader')}>
          <div className={cx('CartItem__spinner')} />
        </div>
      )}
      <div className={cx(`CartItem__inStopList_${currentClassType}__body`)}>
        {!isPhone && (
          <figure
            className={cx(`CartItem__inStopList_${currentClassType}__image`, {
              [`CartItem__inStopList_${currentClassType}__image_preview`]: !image,
            })}
          >
            {image && <img src={image} alt={name} />}
          </figure>
        )}
        <div className={cx(`CartItem__inStopList_${currentClassType}__info`)}>
          <Heading level={5} className={cx(`CartItem__inStopList_${currentClassType}__name`)}>
            {name}
            {isGift && (
              <Gift
                className={cx('CartItem__gift', 'CartItem__inStopList-gift')}
                width={20}
                height={20}
              />
            )}
          </Heading>
          <div className={cx(`CartItem__inStopList_${currentClassType}___message--error`)}>
            <FormattedMessage id="cart.composition.inStopList" />
          </div>
        </div>
        <div className={cx(`CartItem__inStopList_${currentClassType}__buttons`)}>
          {(isGift || isStockCondition) &&
          !isBonus &&
          !isAllConditionGoodsInStopList &&
          !isAllRewardsInStoplist ? (
            <Link to={to} className={cx(`CartItem__inStopList_${currentClassType}__replaceButton`)}>
              <FormattedMessage id="button.replace" />
            </Link>
          ) : (
            <button
              type="button"
              className={cx(`CartItem__inStopList_${currentClassType}__replaceButton`)}
              onClick={onCLickHandlet}
            >
              <FormattedMessage id="button.replace" />
            </button>
          )}
          {!isGift && (
            <button
              className={cx('CartItem__remove')}
              onClick={onRemove}
              disabled={removing}
              type="button"
            >
              <RemoveIcon width={12} height={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

ViewInStopList.defaultProps = {
  className: '',
};

ViewInStopList.propTypes = {
  className: PropTypes.string,
  good: PropTypes.object.isRequired,
};
